'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { FieldFooter } from '@lichtblick/photon/atoms/field-footer';
import { Radio } from '@lichtblick/photon/molecules/radio';
import { Tab, Tabs } from '@lichtblick/photon/molecules/tabs';
import { Textarea } from '@lichtblick/photon/molecules/textarea';
import { Textfield } from '@lichtblick/photon/molecules/textfield';
import { useSearchParams } from 'next/navigation';
import { Suspense, useActionState, useState } from 'react';
import { submitCancellationForm } from './actions';
import styles from './emob-cancellation-form.module.scss';
const EMAIL_MISMATCH_ERROR = 'Die E-Mail-Adressen stimmen nicht überein.';
const INVALID_EMAIL_ERROR = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
const REQUIRED_ERROR_MESSAGE = 'Hier fehlt noch eine Angabe.';
const TERMINATION_DATE_HINT = 'Wenn Sie kein Datum auswählen, kündigen wir zum nächstmöglichen Zeitpunkt.';
const SUCCESS_MESSAGE = 'Ihr Auftrag zur Kündigung bzw. Ihr Widerruf wurde erfolgreich übermittelt. Wir bearbeiten Ihr Anliegen schnellstmöglich und melden uns bei Bedarf bei Ihnen.';
const ERROR_MESSAGE = 'Leider konnte Ihr Auftrag zur Kündigung bzw. Ihr Widerruf nicht übermittelt werden. Bitte versuchen Sie es später erneut.';
const VALIDATION_ERROR_MESSAGE = 'Bitte prüfen Sie Ihre Angaben und versuchen Sie es erneut.';
const SUBMIT_BUTTON_LABEL = {
    cancellation: 'Jetzt Kündigung abschicken',
    revocation: 'Jetzt Widerruf abschicken'
};
const EMAIL_PATTERN = String.raw`^([A-Za-z0-9_.%+\-]+)@([A-Za-z0-9\-]+\.)+([A-Za-z]{2,})$`;
const escapePattern = (value)=>value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const INITIAL_STATE = {
    submitted: false,
    inputs: {
        cancellationReason: '',
        cancellationType: 'cancellation',
        confirmEmail: '',
        customerNumber: '',
        email: '',
        firstName: '',
        lastName: '',
        terminationDate: '',
        terminationType: ''
    }
};
const InnerEmobCancellationForm = ()=>{
    const searchParams = useSearchParams();
    const cancellationTypeUrlParam = searchParams.get('type') === 'revocation' ? 'revocation' : 'cancellation';
    const [cancellationType, setCancellationType] = useState(cancellationTypeUrlParam);
    const [email, setEmail] = useState('');
    const [emailErrorMessage, setEmailErrorMessage] = useState(REQUIRED_ERROR_MESSAGE);
    const [terminationType, setTerminationType] = useState(INITIAL_STATE.inputs.terminationType);
    const [state, dispatch, pending] = useActionState(submitCancellationForm, INITIAL_STATE);
    const inputs = state.inputs;
    const formDisabled = pending || state.submitted;
    const submitButtonLabel = SUBMIT_BUTTON_LABEL[cancellationType];
    return /*#__PURE__*/ _jsx("section", {
        className: styles.section,
        children: /*#__PURE__*/ _jsxs("form", {
            action: dispatch,
            className: styles.form,
            children: [
                /*#__PURE__*/ _jsxs("fieldset", {
                    className: styles.fieldset,
                    disabled: formDisabled,
                    children: [
                        /*#__PURE__*/ _jsx("legend", {
                            className: styles.headline,
                            children: "Art des Anliegens"
                        }),
                        /*#__PURE__*/ _jsxs(Tabs, {
                            defaultValue: cancellationType,
                            name: "cancellationType",
                            onChange: (event)=>setCancellationType(event.currentTarget.value),
                            children: [
                                /*#__PURE__*/ _jsx(Tab, {
                                    label: "Kündigung",
                                    value: "cancellation"
                                }),
                                /*#__PURE__*/ _jsx(Tab, {
                                    label: "Widerruf",
                                    value: "revocation"
                                })
                            ]
                        }, cancellationType)
                    ]
                }),
                cancellationType === 'cancellation' && /*#__PURE__*/ _jsxs("fieldset", {
                    className: styles.fieldset,
                    disabled: formDisabled,
                    children: [
                        /*#__PURE__*/ _jsx("legend", {
                            className: styles.headline,
                            children: "Details zur Kündigung"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles['radio-group'],
                            children: [
                                /*#__PURE__*/ _jsx(Radio, {
                                    checked: terminationType === 'ordinary',
                                    errorMessage: REQUIRED_ERROR_MESSAGE,
                                    label: /*#__PURE__*/ _jsx(Radio.Label, {
                                        children: "Ordentliche Kündigung"
                                    }),
                                    name: "terminationType",
                                    onChange: (event)=>setTerminationType(event.currentTarget.value),
                                    required: true,
                                    value: "ordinary"
                                }),
                                /*#__PURE__*/ _jsx(Radio, {
                                    checked: terminationType === 'extraordinary',
                                    errorMessage: REQUIRED_ERROR_MESSAGE,
                                    label: /*#__PURE__*/ _jsx(Radio.Label, {
                                        children: "Außerordentliche Kündigung"
                                    }),
                                    name: "terminationType",
                                    onChange: (event)=>setTerminationType(event.currentTarget.value),
                                    required: true,
                                    value: "extraordinary"
                                })
                            ]
                        }),
                        terminationType === 'extraordinary' && /*#__PURE__*/ _jsx(Textarea, {
                            defaultValue: inputs.cancellationReason,
                            errorMessage: REQUIRED_ERROR_MESSAGE,
                            label: "Kündigungsgrund",
                            name: "cancellationReason",
                            required: true,
                            rows: 4
                        }),
                        /*#__PURE__*/ _jsx(Textfield, {
                            defaultValue: inputs.terminationDate,
                            footer: TERMINATION_DATE_HINT,
                            label: "Kündigungsdatum",
                            name: "terminationDate",
                            type: "date"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("fieldset", {
                    className: styles.fieldset,
                    disabled: formDisabled,
                    children: [
                        /*#__PURE__*/ _jsx("legend", {
                            className: styles.headline,
                            children: "Persönliche Daten"
                        }),
                        /*#__PURE__*/ _jsx(Textfield, {
                            defaultValue: inputs.firstName,
                            errorMessage: REQUIRED_ERROR_MESSAGE,
                            label: "Vorname",
                            name: "firstName",
                            required: true,
                            type: "text"
                        }),
                        /*#__PURE__*/ _jsx(Textfield, {
                            defaultValue: inputs.lastName,
                            errorMessage: REQUIRED_ERROR_MESSAGE,
                            label: "Nachname",
                            name: "lastName",
                            required: true,
                            type: "text"
                        }),
                        /*#__PURE__*/ _jsx(Textfield, {
                            defaultValue: inputs.customerNumber,
                            errorMessage: REQUIRED_ERROR_MESSAGE,
                            footer: "Sie finden Ihre Kundennummer auf den Vertrags- und Rechnungsdokumenten. Ihre Ladekartennummer finden Sie auf der Rückseite Ihrer Ladekarte.",
                            label: "Kundenummer oder Ladekartennummer",
                            name: "customerNumber",
                            required: true,
                            type: "text"
                        }),
                        /*#__PURE__*/ _jsx(Textfield, {
                            defaultValue: inputs.email,
                            errorMessage: emailErrorMessage,
                            label: "E-Mail-Adresse",
                            name: "email",
                            onChange: (e)=>{
                                setEmail(e.target.value);
                                setEmailErrorMessage(e.target.validity.valueMissing ? REQUIRED_ERROR_MESSAGE : INVALID_EMAIL_ERROR);
                            },
                            required: true,
                            type: "email",
                            pattern: EMAIL_PATTERN,
                            value: email
                        }),
                        /*#__PURE__*/ _jsx(Textfield, {
                            defaultValue: inputs.confirmEmail,
                            errorMessage: EMAIL_MISMATCH_ERROR,
                            label: "E-Mail-Adresse wiederholen",
                            name: "confirmEmail",
                            required: true,
                            pattern: email ? escapePattern(email) : undefined,
                            type: "email"
                        })
                    ]
                }),
                !state.submitted && state.error && !pending && /*#__PURE__*/ _jsx(FieldFooter, {
                    className: `${styles['status-message']} ${styles['status-message-error']}`,
                    error: true,
                    children: state.error === 'InvalidInput' ? VALIDATION_ERROR_MESSAGE : ERROR_MESSAGE
                }),
                state.submitted && /*#__PURE__*/ _jsx(FieldFooter, {
                    className: `${styles['status-message']} ${styles['status-message-success']}`,
                    children: SUCCESS_MESSAGE
                }),
                /*#__PURE__*/ _jsx(BoxCTA, {
                    as: "button",
                    disabled: formDisabled,
                    loading: pending,
                    type: "submit",
                    children: submitButtonLabel
                })
            ]
        })
    });
};
export const EmobCancellationForm = (props)=>/*#__PURE__*/ _jsx(Suspense, {
        children: /*#__PURE__*/ _jsx(InnerEmobCancellationForm, {
            ...props
        })
    });

//# sourceMappingURL=index.js.map